import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './content-switcher-item.scss?inline'

/**
 * `dap-ds-content-switcher-item`
 * @summary Segmented control item component for the segmented control.
 * @element dap-ds-content-switcher-item
 * @title - Content switcher item
 * @group content-switcher
 *
 * @slot iconcontent - The icon content of the segmented control item.
 *
 * @csspart input - The input of the segmented control item.
 *
 * @cssproperty --dds-content-switcher-item-height - The height of the content switcher item (default: var(--dds-spacing-800, 32px))
 * @cssproperty --dds-content-switcher-item-padding - The padding of the content switcher item (default: var(--dds-spacing-200, 8px) var(--dds-spacing-400, 16px))
 * @cssproperty --dds-content-switcher-item-font-size - The font size of the content switcher item (default: var(--dds-size-sm, 14px))
 * @cssproperty --dds-content-switcher-item-font-weight - The font weight of the content switcher item (default: var(--dds-weight-bold, 700))
 * @cssproperty --dds-content-switcher-item-border-radius - The border radius of the content switcher item (default: var(--dds-radius-large, 16px))
 * @cssproperty --dds-content-switcher-item-background - The background color of the content switcher item (default: transparent)
 * @cssproperty --dds-content-switcher-item-color - The text color of the content switcher item (default: var(--dds-text-neutral-base))
 * @cssproperty --dds-content-switcher-item-border-color - The border color of the content switcher item (default: transparent)
 * @cssproperty --dds-content-switcher-item-checked-background - The background color of the checked content switcher item (default: var(--dds-button-primary-background-enabled))
 * @cssproperty --dds-content-switcher-item-checked-color - The text color of the checked content switcher item (default: var(--dds-text-neutral-on-inverted))
 * @cssproperty --dds-content-switcher-item-checked-border-color - The border color of the checked content switcher item (default: var(--dds-border-neutral-transparent-interactive))
 */
export default class DapDSContentSwitcherItem extends DdsElement {
  static tagName = 'dap-ds-content-switcher-item'

  /** The value of the segmented control item. */
  @property({ type: String, reflect: true }) value: string
  /** The checked state of the segmented control item. */
  @property({ type: Boolean }) checked: boolean = false
  /** The disabled state of the segmented control item. */
  @property({ type: Boolean, reflect: true }) disabled: boolean = false
  /** The name of the segmented control item. */
  @property({ type: String, reflect: true }) name: string

  static readonly styles = unsafeCSS(styles)

  render() {
    return html`
      <button
        part="input"
        name=${ifDefined(this.name)}
        value=${ifDefined(this.value)}
        .checked=${this.checked}
        .disabled=${this.disabled}
        aria-label=${ifDefined(this.ariaLabel) || ''}
        aria-pressed=${this.checked}
        .autofocus=${this.autofocus}
        class=${clsx('content-switcher__input', {
          'content-switcher__input--checked': this.checked,
        })}>
        <slot slot="iconcontent"></slot>
      </button>
    `
  }
}
